import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import isNumber from 'lodash/isNumber';
import omit from 'lodash/omit';
import makeStyles from '@mui/styles/makeStyles';
import { grey, orange } from '@mui/material/colors';
import Typography from '@mui/material/Typography';

import { CustomTooltip } from 'ui';
import { useReportPage } from 'shared/hooks';
import BoxPlot from './BoxPlot';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    marginTop: 30,
    minWidth: 'calc(var(--boxplot-width) + 95px)',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    '&:nth-child(2) div:last-child': {
      borderTop: '1px solid #000',
    },
  },
  label: {
    textAlign: 'right',
    fontWeight: 'bold',
    fontSize: useReportPage ? 10 : 'inherit',
    width: 130,
    paddingRight: 30,
    alignItems: 'center',
    position: 'relative',
    '&:after': {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      right: 15,
      content: '""',
      height: useReportPage ? 15 : 25,
      width: 8,
      display: 'inline-block',
      backgroundColor: 'var(--boxplot-legend-color)',
    },
  },
  boxPlotWrapper: {
    borderLeft: `1px solid ${grey[700]}`,
    paddingTop: useReportPage ? 5 : 15,
    paddingBottom: useReportPage ? 5 : 15,
  },
  scaleX: {
    marginLeft: 130,
    width: 'calc(var(--boxplot-width) + 1px)',
    textAlign: 'right',
    display: 'flex',
    position: 'absolute',
    height: '100%',
    fontSize: useReportPage ? 10 : 11,
    '&:before': {
      content: '"0"',
      position: 'absolute',
      top: -25,
      left: 0,
      transform: 'translateX(-50%)',
    },
    '& div': {
      flex: '0 0 25%',
      borderRight: `1px solid ${grey[400]}`,
    },
    '& div span': {
      transform: 'translate(50%, -25px)',
      display: 'inline-block',
    },
  },
  legend: {
    display: 'flex',
    fontSize: useReportPage ? 10 : 12,
    maxWidth: 450,
    marginTop: useReportPage ? 0 : 30,
    marginLeft: useReportPage ? 0 : 115,
    '& > div': {
      paddingLeft: 15,
      paddingRight: 15,
      display: 'flex',
      alignItems: 'center',
    },
  },
  rangeIcon: {
    borderLeft: `1px solid ${grey[400]}`,
    borderRight: `1px solid ${grey[400]}`,
    display: 'block',
    width: 30,
    height: 15,
    marginRight: 15,
    position: 'relative',
    '&:before': {
      content: '""',
      transform: 'translateY(-50%)',
      borderTop: `1px solid ${grey[400]}`,
      position: 'absolute',
      left: 0,
      right: 0,
      top: '50%',
    },
  },
  medianIcon: {
    width: 15,
    height: 15,
    marginRight: 15,
    display: 'block',
    backgroundColor: theme.palette.primary.main,
    position: 'relative',
    '&:before': {
      content: '""',
      transform: 'translateX(-50%)',
      borderRight: '1px dashed #fff',
      position: 'absolute',
      left: '50%',
      bottom: 0,
      top: 0,
    },
  },
  valueIcon: {
    width: 10,
    height: 10,
    marginRight: 15,
    display: 'block',
    backgroundColor: orange[700],
    borderRadius: '50%',
  },
}));

const getMinAndMax = data => {
  const values = data
    .map(element => Object.values(omit(element, 'outliersTooltip')).flat())
    .flat()
    .filter(value => isNumber(value));
  return {
    min: Math.floor(Math.min(...values)),
    max: Math.ceil(Math.max(...values)),
  };
};
const prepareDataToDisplay = value => `Cents ${value?.toFixed(2)}`;

const BoxPlotChart = ({ stats, width, height }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { min, max } = getMinAndMax(stats);
  const factor = max / 4;

  const scaleX = [];

  for (let i = 1; i < 5; i += 1) {
    scaleX.push(
      <div key={i}>
        <span>{factor * i}</span>
      </div>,
    );
  }

  return (
    <>
      <div className={classes.container}>
        <div className={classes.scaleX} style={{ '--boxplot-width': `${width}px` }}>
          {scaleX}
        </div>
        {stats.map(({ color, title, ...itemStats }) => {
          return (
            <CustomTooltip
              placement="top"
              key={title}
              title={
                <>
                  <Typography variant="h6">{t(title)}</Typography>
                  <Typography variant="caption">
                    {t('Company value')}:{' '}
                    <strong>{prepareDataToDisplay(itemStats.outliersTooltip)}</strong>
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    {t('Lower quartile')}:{' '}
                    <strong>{prepareDataToDisplay(itemStats.quartile1)}</strong>
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    {t('Peers median value')}:{' '}
                    <strong>{prepareDataToDisplay(itemStats.quartile2)}</strong>
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    {t('Upper quartile')}:{' '}
                    <strong>{prepareDataToDisplay(itemStats.quartile3)}</strong>
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    {t('Peers max value')}:{' '}
                    <strong>{prepareDataToDisplay(itemStats.whiskerHigh)}</strong>
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    {t('Peers min value')}:{' '}
                    <strong>{prepareDataToDisplay(itemStats.whiskerLow)}</strong>
                  </Typography>
                </>
              }
              arrow
            >
              <div className={classes.row}>
                <div className={classes.label} style={{ '--boxplot-legend-color': color }}>
                  {title}
                </div>
                <div className={classes.boxPlotWrapper}>
                  <BoxPlot stats={itemStats} width={width} height={height} min={min} max={max} />
                </div>
              </div>
            </CustomTooltip>
          );
        })}
      </div>
      <div className={classes.legend}>
        <div>
          <span className={classes.rangeIcon} />
          {t('Range')}
        </div>
        <div>
          <span className={classes.medianIcon} />
          {t('Peer group median')}
        </div>
        <div>
          <span className={classes.valueIcon} />
          {t('Company value')}
        </div>
      </div>
    </>
  );
};

BoxPlotChart.defaultProps = {
  width: 300,
  height: 25,
};

BoxPlotChart.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      whiskerLow: PropTypes.number,
      whiskerHigh: PropTypes.number,
      outliers: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
      outliersTooltip: PropTypes.number,
      quartile1: PropTypes.number,
      quartile2: PropTypes.number,
      quartile3: PropTypes.number,
      title: PropTypes.string,
      color: PropTypes.string,
    }),
  ).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default BoxPlotChart;
